import {
  getProductComponents,
  getProducts,
  updateProductComponents,
  type ProductComponent,
} from '@/api/products';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { useProduct, useProductCategories } from '@/hooks/use-products';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LinkedPackagesSection } from './components/LinkedPackagesSection';
import { PRODUCT_STATUSES } from './mock-data';
import type { ProductStatus } from './types';

type TabType = 'overview' | 'stock' | 'pricing' | 'rental' | 'components' | 'history';

export function ProductDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const { product, isLoading, error, refetch } = useProduct(id);
  const { categories } = useProductCategories();

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <Card className="w-96 text-center">
          <CardContent className="pt-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <Card className="w-96 text-center">
          <CardContent className="pt-6">
            <p className="text-red-500 mb-4">{t('product.errorPrefix', { message: error })}</p>
            <div className="flex gap-2 justify-center">
              <Button onClick={() => refetch()}>{t('common.retry')}</Button>
              <Button variant="outline" onClick={() => navigate('/products')}>
                {t('product.backToList')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not found state
  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center kgc-bg">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('product.productNotFound')}
          </h1>
          <Button onClick={() => navigate('/products')} className="mt-4">
            {t('product.backToList')}
          </Button>
        </div>
      </div>
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('hu-HU');
  };

  const getStatusBadge = (status: ProductStatus) => {
    const config = PRODUCT_STATUSES.find(s => s.value === status);
    return (
      <span className={`rounded-full px-3 py-1 text-sm font-medium ${config?.color ?? ''}`}>
        {config?.label ?? status}
      </span>
    );
  };

  const getCategoryBadge = (category: string) => {
    // A DTO `category` mezője a `ProductCategory.code` — feloldás az élő listából.
    const match = categories.find(c => c.code === category);
    return (
      <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 dark:bg-slate-700 dark:text-gray-200">
        {match?.name ?? category}
      </span>
    );
  };

  const getStockStatusColor = () => {
    if (product.stockQuantity === 0) return 'text-red-600 dark:text-red-400';
    if (product.stockQuantity < product.minStockLevel)
      return 'text-orange-600 dark:text-orange-400';
    return 'text-green-600 dark:text-green-400';
  };

  // A „Bérlés" fül a kategórián múlik. A `product.category` a valós
  // `ProductCategory.code` — a KGC élő fájában jelenleg nincs bérgép-ág,
  // így a fül rejtve marad (korábban a `'rental_equipment'` mock-értékre
  // hasonlított, ami sosem érkezett meg a wire-en).
  const isRentalEquipment = /BER/i.test(product.category ?? '');

  const tabs = (
    [
      { id: 'overview' as const, label: t('product.tabs.overview'), show: true },
      { id: 'stock' as const, label: t('product.tabs.stock'), show: true },
      { id: 'pricing' as const, label: t('product.tabs.pricing'), show: true },
      { id: 'rental' as const, label: t('product.tabs.rental'), show: isRentalEquipment },
      { id: 'components' as const, label: t('product.tabs.components'), show: true },
      { id: 'history' as const, label: t('product.tabs.history'), show: true },
    ] as const
  ).filter(tab => tab.show);

  return (
    <div className="min-h-screen kgc-bg">
      {/* Header */}
      <header className="shadow-sm kgc-card-bg">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/products')}>
                {t('common.back')}
              </Button>
              <div className="flex items-center gap-4">
                {product.thumbnailUrl ? (
                  <img
                    src={product.thumbnailUrl}
                    alt={product.name}
                    className="h-12 w-12 rounded object-cover"
                  />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded bg-gray-100 text-2xl dark:bg-slate-700">
                    📦
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {product.name}
                    </h1>
                    {getStatusBadge(product.status)}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {product.sku}
                    {product.brand && ` • ${product.brand}`}
                    {product.model && ` ${product.model}`}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate(`/products/${product.id}/edit`)}>
                {t('common.edit')}
              </Button>
              {!isRentalEquipment && (
                <Button
                  className="bg-primary hover:bg-primary/90"
                  onClick={() => navigate('/sales/new')}
                >
                  + {t('product.sale')}
                </Button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-4 flex gap-1 border-b border-gray-200 dark:border-slate-700">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-b-2 border-primary text-primary'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {activeTab === 'overview' && (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Left column - Main info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic info */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('product.basicData')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('product.sku')}
                      </label>
                      <p className="text-gray-900 dark:text-gray-100 font-mono">{product.sku}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('common.category')}
                      </label>
                      <div className="mt-1">{getCategoryBadge(product.category)}</div>
                    </div>
                    {product.barcode && (
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          {t('product.barcode')}
                        </label>
                        <p className="text-gray-900 dark:text-gray-100 font-mono">
                          {product.barcode}
                        </p>
                      </div>
                    )}
                    {/* Q-T159: a „Gyártó kód" sor eltávolítva — a mögötte lévő mező sosem
                        létezett az adatbázisban, így élesben mindig üresen maradt (a feltétel
                        soha nem teljesült). A gyári cikkszámot a `manufacturerCode` hordozza. */}
                    {product.brand && (
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          {t('product.brand')}
                        </label>
                        <p className="text-gray-900 dark:text-gray-100">{product.brand}</p>
                      </div>
                    )}
                    {product.model && (
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          {t('common.type')}
                        </label>
                        <p className="text-gray-900 dark:text-gray-100">{product.model}</p>
                      </div>
                    )}
                  </div>
                  {product.description && (
                    <div className="mt-4">
                      <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {t('product.description')}
                      </label>
                      <p className="mt-1 text-gray-900 dark:text-gray-100">{product.description}</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Supplier info */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('product.supplier')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {product.supplier ? (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          {t('product.supplierName')}
                        </label>
                        <p className="text-gray-900 dark:text-gray-100">{product.supplier.name}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          {t('product.supplierCode')}
                        </label>
                        <p className="text-gray-900 dark:text-gray-100">{product.supplier.code}</p>
                      </div>
                      {product.lastPurchaseDate && (
                        <div>
                          <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            {t('product.lastOrder')}
                          </label>
                          <p className="text-gray-900 dark:text-gray-100">
                            {formatDate(product.lastPurchaseDate)}
                          </p>
                        </div>
                      )}
                      {product.lastPurchasePrice && (
                        <div>
                          <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            {t('product.lastPurchasePrice')}
                          </label>
                          <p className="text-gray-900 dark:text-gray-100">
                            {formatPrice(product.lastPurchasePrice)}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">{t('product.noSupplier')}</p>
                  )}
                </CardContent>
              </Card>

              {isRentalEquipment && <LinkedPackagesSection productId={product.id} />}
            </div>

            {/* Right column - Stats & Quick actions */}
            <div className="space-y-6">
              {/* Quick stats */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('product.quickOverview')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('product.sellingPriceGross')}:
                      </span>
                      <span className="font-bold text-gray-900 dark:text-gray-100">
                        {formatPrice(product.sellingPriceGross)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('product.inStock')}
                      </span>
                      <span className={`font-bold ${getStockStatusColor()}`}>
                        {product.availableQuantity} {t('common.piece')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500 dark:text-gray-400">
                        {t('product.reserved')}
                      </span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {product.reservedQuantity} {t('common.piece')}
                      </span>
                    </div>
                    {product.location && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          {t('product.locationCode')}:
                        </span>
                        <span className="font-mono text-gray-900 dark:text-gray-100">
                          {product.location}
                        </span>
                      </div>
                    )}
                    <div className="border-t pt-4 dark:border-slate-700">
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          {t('product.margin')}
                        </span>
                        <span className="font-medium text-green-600 dark:text-green-400">
                          {product.marginPercent.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick actions */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('admin.quickActions')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => navigate('/goods-receipt/new')}
                    >
                      📦 {t('product.goodsReceipt')}
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => navigate('/inventory/movements')}
                    >
                      📋 {t('product.movements')}
                    </Button>
                    {!isRentalEquipment && (
                      <Button
                        variant="outline"
                        className="w-full justify-start"
                        onClick={() => navigate('/sales/new')}
                      >
                        🛒 {t('product.sale')}
                      </Button>
                    )}
                    {isRentalEquipment && (
                      <Button
                        variant="outline"
                        className="w-full justify-start"
                        onClick={() => navigate('/rental/new')}
                      >
                        🔧 {t('product.startRental')}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'stock' && (
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>{t('product.stockData')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.totalStock')}
                    </span>
                    <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {product.stockQuantity} {t('common.piece')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.reserved')}
                    </span>
                    <span className="text-xl font-medium text-orange-600 dark:text-orange-400">
                      {product.reservedQuantity} {t('common.piece')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.available')}
                    </span>
                    <span className={`text-xl font-bold ${getStockStatusColor()}`}>
                      {product.availableQuantity} {t('common.piece')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.minimumLevel')}
                    </span>
                    <span className="text-gray-900 dark:text-gray-100">
                      {product.minStockLevel} {t('common.piece')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.reorderQuantity')}:
                    </span>
                    <span className="text-gray-900 dark:text-gray-100">
                      {product.reorderQuantity} {t('common.piece')}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('product.placement')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      {t('product.locationCode')}
                    </label>
                    <p className="text-2xl font-mono text-gray-900 dark:text-gray-100">
                      {product.location ?? t('product.notSpecified')}
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-slate-700/50">
                    <p
                      className="text-sm text-gray-600 dark:text-gray-300"
                      dangerouslySetInnerHTML={{ __html: t('product.locationCodeFormat') }}
                    />
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {t('product.locationCodeExample')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {product.stockQuantity < product.minStockLevel && (
              <Card className="lg:col-span-2 border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-900/20">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">⚠️</span>
                    <div>
                      <p className="font-medium text-orange-800 dark:text-orange-200">
                        {t('product.lowStockWarning')}
                      </p>
                      <p className="text-sm text-orange-600 dark:text-orange-300">
                        {t('product.lowStockDescription', {
                          current: String(product.stockQuantity),
                          min: String(product.minStockLevel),
                          reorder: String(product.reorderQuantity),
                        })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {activeTab === 'pricing' && (
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>{t('product.prices')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.purchasePriceNet')}:
                    </span>
                    <span className="text-xl font-medium text-gray-900 dark:text-gray-100">
                      {formatPrice(product.purchasePrice)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.sellingPriceNet')}:
                    </span>
                    <span className="text-xl font-medium text-gray-900 dark:text-gray-100">
                      {formatPrice(product.sellingPriceNet)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.vatRate')}:
                    </span>
                    <span className="text-gray-900 dark:text-gray-100">{product.vatRate}%</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.sellingPriceGross')}:
                    </span>
                    <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                      {formatPrice(product.sellingPriceGross)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('product.marginCalculation')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.marginFt')}
                    </span>
                    <span className="text-xl font-medium text-green-600 dark:text-green-400">
                      {formatPrice(product.sellingPriceNet - product.purchasePrice)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.marginPercent')}
                    </span>
                    <span className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {product.marginPercent.toFixed(1)}%
                    </span>
                  </div>
                </div>
                <div className="mt-4 rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    {t('product.marginFormula')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'rental' && isRentalEquipment && (
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>{t('product.rentalRates')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.dailyRate')}
                    </span>
                    <span className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {product.rentalDailyRate ? formatPrice(product.rentalDailyRate) : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.weeklyRate')}
                    </span>
                    <span className="text-xl font-medium text-gray-900 dark:text-gray-100">
                      {product.rentalWeeklyRate ? formatPrice(product.rentalWeeklyRate) : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.monthlyRate')}
                    </span>
                    <span className="text-xl font-medium text-gray-900 dark:text-gray-100">
                      {product.rentalMonthlyRate ? formatPrice(product.rentalMonthlyRate) : '-'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.depositAmount')}
                    </span>
                    <span className="text-xl font-medium text-orange-600 dark:text-orange-400">
                      {product.rentalDepositAmount ? formatPrice(product.rentalDepositAmount) : '-'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('product.rentalStatus')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.totalMachines')}
                    </span>
                    <span className="text-xl font-medium text-gray-900 dark:text-gray-100">
                      {product.stockQuantity} {t('common.piece')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b dark:border-slate-700">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.rentedOut')}
                    </span>
                    <span className="text-xl font-medium text-orange-600 dark:text-orange-400">
                      {product.reservedQuantity} {t('common.piece')}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600 dark:text-gray-300">
                      {t('product.availableForRental')}
                    </span>
                    <span className={`text-xl font-bold ${getStockStatusColor()}`}>
                      {product.availableQuantity} {t('common.piece')}
                    </span>
                  </div>
                </div>

                <Button
                  className="mt-4 w-full bg-primary hover:bg-primary/90"
                  onClick={() => navigate('/rental/new')}
                >
                  🔧 {t('product.startNewRental')}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'components' && (
          <ProductComponentsTab productId={product.id} sellingPrice={product.sellingPriceNet} />
        )}

        {activeTab === 'history' && (
          <Card>
            <CardHeader>
              <CardTitle>{t('product.history')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="rounded-lg border p-4 dark:border-slate-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(product.updatedAt)}
                  </p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {t('product.productUpdated')}
                  </p>
                </div>
                {product.lastPurchaseDate && (
                  <div className="rounded-lg border p-4 dark:border-slate-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(product.lastPurchaseDate)}
                    </p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {t('product.lastPurchase', {
                        price: formatPrice(product.lastPurchasePrice ?? 0),
                      })}
                    </p>
                  </div>
                )}
                <div className="rounded-lg border p-4 dark:border-slate-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(product.createdAt)}
                  </p>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    {t('product.productCreated', { user: product.createdBy ?? '' })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}

// ============================================
// SALES-FG-4-5: ProductComponentsTab
// ============================================

function ProductComponentsTab({
  productId,
  sellingPrice,
}: {
  productId: string;
  sellingPrice: number;
}) {
  const { t } = useTranslation();
  const [components, setComponents] = useState<ProductComponent[]>([]);
  const [bomCost, setBomCost] = useState(0);
  const [margin, setMargin] = useState(0);
  const [marginPercent, setMarginPercent] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<
    Array<{ id: string; name: string; sku: string; purchasePrice: number }>
  >([]);
  const [addQuantity, setAddQuantity] = useState(1);

  const fetchComponents = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getProductComponents(productId);
      setComponents(response.data.components);
      setBomCost(response.data.totalBomCost);
      setMargin(response.data.margin);
      setMarginPercent(response.data.marginPercent);
    } catch (err) {
      console.error('Error fetching components:', err);
    } finally {
      setIsLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    void fetchComponents();
  }, [fetchComponents]);

  const handleSearch = useCallback(
    async (term: string) => {
      setSearchTerm(term);
      if (term.length < 2) {
        setSearchResults([]);
        return;
      }
      try {
        const response = await getProducts({ search: term, pageSize: 10 });
        setSearchResults(
          response.data
            .filter(p => p.id !== productId)
            .map(p => ({
              id: p.id,
              name: p.name,
              sku: p.sku,
              purchasePrice: p.purchasePrice,
            }))
        );
      } catch {
        setSearchResults([]);
      }
    },
    [productId]
  );

  const handleAddComponent = useCallback(
    async (compProductId: string) => {
      const existingEntries = components.map(c => ({
        productId: c.productId,
        quantity: c.quantity,
      }));
      const existingIdx = existingEntries.findIndex(e => e.productId === compProductId);
      if (existingIdx >= 0) {
        const entry = existingEntries[existingIdx];
        if (entry) {
          entry.quantity += addQuantity;
        }
      } else {
        existingEntries.push({ productId: compProductId, quantity: addQuantity });
      }

      try {
        await updateProductComponents(productId, existingEntries);
        await fetchComponents();
        setShowAddModal(false);
        setSearchTerm('');
        setSearchResults([]);
        setAddQuantity(1);
      } catch (err) {
        console.error('Error adding component:', err);
      }
    },
    [productId, components, addQuantity, fetchComponents]
  );

  const handleRemoveComponent = useCallback(
    async (compProductId: string) => {
      const filtered = components
        .filter(c => c.productId !== compProductId)
        .map(c => ({ productId: c.productId, quantity: c.quantity }));
      try {
        await updateProductComponents(productId, filtered);
        await fetchComponents();
      } catch (err) {
        console.error('Error removing component:', err);
      }
    },
    [productId, components, fetchComponents]
  );

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      maximumFractionDigits: 0,
    }).format(price);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            <span className="ml-2 text-muted-foreground">{t('common.loading')}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* BOM Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t('product.explodedView.title')}</CardTitle>
            <Button onClick={() => setShowAddModal(true)}>
              + {t('product.explodedView.addComponent')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {components.length === 0 ? (
            <p className="py-8 text-center text-muted-foreground">
              {t('product.explodedView.noComponents')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('product.explodedView.componentName')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('product.sku')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('product.explodedView.quantityPerUnit')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('product.explodedView.unitCost')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {t('product.explodedView.totalCost')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {components.map(comp => (
                    <tr key={comp.productId} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-foreground">
                        {comp.productName}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-mono text-muted-foreground">
                        {comp.sku}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                        {comp.quantity}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-muted-foreground">
                        {formatPrice(comp.unitPrice)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-foreground">
                        {formatPrice(comp.totalPrice)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button
                          onClick={() => handleRemoveComponent(comp.productId)}
                          className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                        >
                          {t('common.delete')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 dark:bg-gray-800">
                    <td
                      colSpan={4}
                      className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-foreground"
                    >
                      {t('product.explodedView.totalBomCost')}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-bold text-foreground">
                      {formatPrice(bomCost)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Margin Summary */}
      <Card>
        <CardHeader>
          <CardTitle>{t('product.explodedView.marginSummary')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-4">
            <div>
              <p className="text-sm text-muted-foreground">
                {t('product.explodedView.totalBomCost')}
              </p>
              <p className="text-xl font-bold text-foreground">{formatPrice(bomCost)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('product.sellingPriceNet')}</p>
              <p className="text-xl font-bold text-foreground">{formatPrice(sellingPrice)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('product.marginFt')}</p>
              <p
                className={`text-xl font-bold ${margin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
              >
                {formatPrice(margin)}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('product.marginPercent')}</p>
              <p
                className={`text-xl font-bold ${marginPercent >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
              >
                {marginPercent.toFixed(1)}%
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Component Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-lg mx-4">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>{t('product.explodedView.addComponent')}</CardTitle>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setSearchTerm('');
                    setSearchResults([]);
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  X
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t('product.explodedView.searchProduct')}
                  </label>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={e => void handleSearch(e.target.value)}
                    placeholder={t('product.searchPlaceholder')}
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    {t('product.explodedView.quantityPerUnit')}
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={addQuantity}
                    onChange={e => setAddQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    className="mt-1 w-24 rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>

                {searchResults.length > 0 && (
                  <div className="max-h-60 overflow-y-auto rounded-md border dark:border-gray-700">
                    {searchResults.map(result => (
                      <button
                        key={result.id}
                        onClick={() => void handleAddComponent(result.id)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800 border-b last:border-b-0 dark:border-gray-700"
                      >
                        <p className="text-sm font-medium text-foreground">{result.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {result.sku} — {formatPrice(result.purchasePrice)}
                        </p>
                      </button>
                    ))}
                  </div>
                )}

                {searchTerm.length >= 2 && searchResults.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    {t('product.noResults')}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
